import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Ban, Heart, Cpu, Eye, Receipt, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';

const principles = [
  {
    number: '01',
    icon: Ban,
    title: 'No Infinite Scroll',
    description: 'HumaClub is designed for intentional engagement, not addictive consumption. You come here with purpose \u2014 to discover causes, verify impact, and take meaningful action. We will never design features that exploit attention or encourage mindless scrolling.'
  },
  {
    number: '02',
    icon: Heart,
    title: 'No Like Counts as Dopamine Drivers',
    description: 'Impact is not measured in likes. We don\'t display vanity metrics that create social comparison or performative giving. The only numbers that matter here are dollars delivered, meals served, and lives changed \u2014 verified on-chain.'
  },
  {
    number: '03',
    icon: Cpu,
    title: 'No Algorithmic Manipulation',
    description: 'We don\'t use recommendation algorithms designed to maximize engagement at the expense of your autonomy. What you see on HumaClub is determined by relevance, verification status, and your explicit preferences \u2014 not by what keeps you scrolling longest.'
  },
  {
    number: '04',
    icon: Eye,
    title: 'Verified-Only Content',
    description: 'Every claim on HumaClub \u2014 every donation amount, every beneficiary count, every volunteer hour \u2014 is backed by on-chain data. If it can\'t be verified, it doesn\'t appear here. We believe trust is built through proof, not promises.'
  },
  {
    number: '05',
    icon: Receipt,
    title: 'Receipts by Default',
    description: 'Every transaction on HumaClub generates a blockchain receipt that anyone can verify independently. This isn\'t an optional feature \u2014 it\'s the foundation of how the platform works. Transparency isn\'t a toggle; it\'s a guarantee.'
  },
  {
    number: '06',
    icon: Lock,
    title: 'Privacy-First for Beneficiaries',
    description: 'While donor and NGO activity is transparent, beneficiary data is protected by design. The people receiving aid through HumaClub-verified projects are never exposed, identified, or turned into content. Their dignity is non-negotiable.'
  },
];

export default function PrinciplesPage() {
  return (
    <div className="min-h-screen" data-testid="principles-page">
      <div className="bg-white py-12 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Link to="/">
              <Button variant="ghost" className="text-sm text-gray-500 hover:text-[#111827] mb-8 -ml-2 font-body" data-testid="principles-back">
                <ArrowLeft className="w-4 h-4 mr-1" /> Back to Home
              </Button>
            </Link>
            <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#7C3AED] mb-6 font-body">
              Our Principles
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-semibold text-[#111827] mb-6 font-heading">
              The Glass Box<br />Principles
            </h1>
            <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed max-w-2xl font-body">
              Six commitments that define how HumaClub operates. These aren&apos;t aspirational guidelines &mdash; they&apos;re architectural decisions baked into the platform.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="space-y-0">
          {principles.map((principle, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="relative"
              data-testid={`principle-${i + 1}`}
            >
              <div className="py-12 lg:py-16 border-b border-gray-100 last:border-b-0">
                <div className="flex gap-6 lg:gap-12">
                  <div className="shrink-0">
                    <span className="text-7xl lg:text-8xl font-heading font-semibold text-gray-100 select-none leading-none">
                      {principle.number}
                    </span>
                  </div>
                  <div className="pt-2">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#7C3AED]/10 to-[#2563EB]/10 flex items-center justify-center shrink-0">
                        <principle.icon className="w-5 h-5 text-[#7C3AED]" />
                      </div>
                      <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-[#111827] font-heading">
                        {principle.title}
                      </h2>
                    </div>
                    <p className="text-base text-[#4B5563] leading-relaxed max-w-xl font-body">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="bg-[#F3F4F6] py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-base lg:text-lg text-[#4B5563] leading-relaxed mb-8 max-w-2xl mx-auto font-body">
              These principles aren't marketing copy. They're the foundation of a platform built to restore trust in humanitarian giving.
            </p>
            <Link to="/explore">
              <Button className="bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full px-8 py-3 text-base font-medium hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 border-0 h-auto" data-testid="principles-explore-cta">
                Explore Verified Projects
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
